import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Layers3,
  Loader2,
  MousePointerClick,
  PlayCircle,
  Search,
  X,
} from 'lucide-react'
import { adminApi } from '@/api/admin'
import type { AdminContent, HierarchyLevel } from '@/api/admin'

const ALL_HIERARCHY_LEVELS: HierarchyLevel[] = ['program', 'curriculum', 'course', 'module', 'lesson']

const LEVEL_LABELS: Record<HierarchyLevel, string> = {
  program: '프로그램',
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
  lesson: '레슨',
}

const LEVEL_STYLES: Record<HierarchyLevel, string> = {
  program: 'bg-violet-50 text-violet-700 ring-violet-200',
  curriculum: 'bg-blue-50 text-blue-700 ring-blue-200',
  course: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
  module: 'bg-teal-50 text-teal-700 ring-teal-200',
  lesson: 'bg-amber-50 text-amber-700 ring-amber-200',
}

const LEVEL_ICONS: Record<HierarchyLevel, React.ElementType> = {
  program: Layers3,
  curriculum: BookOpen,
  course: FolderOpen,
  module: FolderOpen,
  lesson: PlayCircle,
}

export function ContentLevelBadge({ level }: { level: HierarchyLevel }) {
  const Icon = LEVEL_ICONS[level]

  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ring-1 ring-inset ${LEVEL_STYLES[level]}`}>
      <Icon className="h-3.5 w-3.5" />
      {LEVEL_LABELS[level]}
    </span>
  )
}

interface ContentPickerModalProps {
  onClose: () => void
  onSelect: (content: AdminContent) => void
  allowedLevels?: HierarchyLevel[]
  title?: string
  description?: string
}

export function ContentPickerModal({
  onClose,
  onSelect,
  allowedLevels = ALL_HIERARCHY_LEVELS,
  title = '콘텐츠 선택',
  description = '제목으로 검색하거나 콘텐츠 유형을 좁혀 선택하세요.',
}: ContentPickerModalProps) {
  const fixedLevel = allowedLevels.length === 1 ? allowedLevels[0] : ''
  const [keywordInput, setKeywordInput] = useState('')
  const [keyword, setKeyword] = useState('')
  const [hierarchyLevel, setHierarchyLevel] = useState<HierarchyLevel | ''>(fixedLevel)
  const [page, setPage] = useState(0)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin', 'content-picker', keyword, hierarchyLevel, page],
    queryFn: () => adminApi.getContents({
      keyword: keyword || undefined,
      hierarchyLevel: hierarchyLevel || undefined,
      page,
      size: 12,
    }),
  })

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault()
    setPage(0)
    setKeyword(keywordInput.trim())
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="content-picker-title"
        className="flex max-h-[82vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/60 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <h4 id="content-picker-title" className="text-lg font-bold text-slate-900">{title}</h4>
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="콘텐츠 선택 닫기"
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <form onSubmit={handleSearch} className="flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50/70 px-6 py-4">
          <div className="relative min-w-64 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={keywordInput}
              onChange={(event) => setKeywordInput(event.target.value)}
              placeholder="콘텐츠 제목 검색"
              className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          {allowedLevels.length > 1 && (
            <select
              value={hierarchyLevel}
              onChange={(event) => {
                setHierarchyLevel(event.target.value as HierarchyLevel | '')
                setPage(0)
              }}
              aria-label="콘텐츠 유형"
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              {allowedLevels.length === ALL_HIERARCHY_LEVELS.length && <option value="">모든 유형</option>}
              {allowedLevels.map((level) => (
                <option key={level} value={level}>{LEVEL_LABELS[level]}</option>
              ))}
            </select>
          )}
          <button
            type="submit"
            className="h-10 rounded-lg bg-slate-900 px-5 text-sm font-semibold text-white transition hover:bg-slate-700"
          >
            검색
          </button>
        </form>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-3">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" /> 콘텐츠를 불러오는 중입니다
            </div>
          ) : data?.content.length === 0 ? (
            <div className="py-16 text-center">
              <Search className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-600">검색 결과가 없습니다</p>
              <p className="mt-1 text-xs text-slate-400">검색어를 변경해 보세요.</p>
            </div>
          ) : (
            <div className={`divide-y divide-slate-100 ${isFetching ? 'opacity-60' : ''}`}>
              {data?.content.map((content) => {
                const curriculumAncestors = content.curriculumAncestors ?? []

                return (
                  <button
                    key={content.id}
                    type="button"
                    onClick={() => onSelect(content)}
                    className="group flex w-full items-start gap-4 rounded-lg px-2 py-3 text-left transition-colors hover:bg-blue-50/70"
                  >
                    <ContentLevelBadge level={content.hierarchyLevel} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800 group-hover:text-blue-800">
                        {content.title}
                      </span>
                      {content.hierarchyLevel !== 'program' && content.hierarchyLevel !== 'curriculum' ? (
                        <span className="mt-1.5 flex items-start gap-1.5">
                          <BookOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                          <span className="flex min-w-0 flex-wrap items-center gap-1 text-xs">
                            <span className="mr-0.5 text-slate-400">소속 커리큘럼</span>
                            {curriculumAncestors.length > 0 ? (
                              curriculumAncestors.map((curriculum) => (
                                <span
                                  key={curriculum.id}
                                  className="inline-flex max-w-full items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600 ring-1 ring-inset ring-slate-200"
                                  title={`${curriculum.title} · Content #${curriculum.id}`}
                                >
                                  <span className="truncate">{curriculum.title}</span>
                                  <span className="shrink-0 font-normal tabular-nums text-slate-400">#{curriculum.id}</span>
                                </span>
                              ))
                            ) : (
                              <span className="font-medium text-amber-600">연결 정보 없음</span>
                            )}
                          </span>
                        </span>
                      ) : null}
                      <span className="mt-0.5 block text-xs text-slate-400">
                        Content #{content.id}{content.isActive ? '' : ' · 비활성'}
                      </span>
                    </span>
                    <MousePointerClick className="mt-1 h-4 w-4 shrink-0 text-slate-300 group-hover:text-blue-500" />
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-3">
          <span className="text-xs text-slate-500">총 {data?.totalElements ?? 0}개</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              disabled={page === 0}
              aria-label="이전 페이지"
              className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-20 text-center text-xs font-medium text-slate-600">
              {page + 1} / {Math.max(data?.totalPages ?? 1, 1)}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={!data || data.last}
              aria-label="다음 페이지"
              className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
